import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useBetaData } from '../../../contexts/BetaDataContext';
import { getTicketsForDailyReport } from '../../../api/tickets';
import { getWorkItemsForDailyReport } from '../../../api/workItems';
import { compareWorkItemSiblings } from '../../../constants/workItem';
import { getTodayUnassignedTicketIds, getTodayUnassignedWorkItemIds } from '../../../api/auditLog';
import { getDeliverableItemsForUser } from '../../../api/deliverables';
import { getDailyReportVisibleMembers } from '../../../api/departments';
import { getProjectProfile, updateUserSettings } from '../../../api/profiles';
import { useToast } from '../../common/Toast';
import { useConfirm } from '../../common/ConfirmDialog';
import MarkdownViewer from '../../common/MarkdownViewer';
import Avatar from '../../common/Avatar';
import SvgIcon from '../../common/SvgIcon';
import refreshSvg from '../../../assets/icons/actions/refresh.svg?raw';
import MemberVisibilityModal from './MemberVisibilityModal';
import BetaDailyReportProjectSelector from './BetaDailyReportProjectSelector';
import CustomSelect from '../../common/CustomSelect';
import {
  useDailyReportSession,
  HOST_IDLE_END_MS,
  RECONNECT_GIVE_UP_MS,
  PRESENTER_NO_RESPONSE_MS,
} from '../../../hooks/useDailyReportSession';
import './BetaDailyReportView.scss';

// 未分組的 key,對齊工作項目列表的慣例
const UNGROUPED_KEY = '__ungrouped__';

// 群組排序:未分組一律最後。日報用 DB 的 sort_order(全域),不用工作項目列表的個人排序 ——
// 否則同一份報告每個人看到的群組順序不同,報告時會對不上。
//
// 注意 sort_order 目前實際上全部是 0(從沒被寫過,列表的群組排序存在個人設定裡),
// 所以真正在決定順序的是名稱那一段。沒有這個 tiebreaker 群組順序就是不確定的。
const groupKeyOf = (w) => (w.group?.id ?? UNGROUPED_KEY);
const groupNameOf = (w) => (w.group?.name || '未分組');
const compareByGroup = (a, b) => {
  const ra = a.group ? (a.group.sort_order ?? 0) : Number.MAX_SAFE_INTEGER;
  const rb = b.group ? (b.group.sort_order ?? 0) : Number.MAX_SAFE_INTEGER;
  if (ra !== rb) return ra - rb;
  if (!a.group || !b.group) return 0; // 都是未分組
  return String(a.group.name || '').localeCompare(String(b.group.name || ''), 'zh-Hant', { numeric: true });
};

const BetaDailyReportView = ({ onClose, onNavigate }) => {
  const toast = useToast();
  const { confirm } = useConfirm();
  const {
    profile,
    viewingCompanyId,
    isSuperAdmin,
    isCompanyAdmin,
    isDepartmentAdmin,
  } = useAuth();
  const { companyUsers, usersMapByEmail, statusOptions, departments } = useBetaData();

  // 部門選擇器（公司管理員+）
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);

  // 成員
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);

  // 資料
  const [memberProjects, setMemberProjects] = useState([]);
  const [memberDeliverableItems, setMemberDeliverableItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modals
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [showMemberVisibility, setShowMemberVisibility] = useState(false);

  // 展開/收合
  const [expandedTickets, setExpandedTickets] = useState(new Set());
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [activeTab, setActiveTab] = useState('all');
  const [hiddenTicketIds, setHiddenTicketIds] = useState(new Set());
  const [showHiddenList, setShowHiddenList] = useState(false);

  // 每次資料載入完成 +1。報告人會把它放進畫面狀態，主持人看到變了就重撈（例如報告人按了重新整理）
  const [dataVersion, setDataVersion] = useState(0);

  // === 取得有效的部門 ID ===
  // admin+：用 DailyReport 本地的部門選擇器
  // 一般成員/部門管理員：直接用自己的 profile.department_id
  // 也決定簡報進哪個部門的 lobby
  const effectiveDepartmentId = useMemo(() => {
    if (isCompanyAdmin() || isSuperAdmin()) {
      return selectedDepartmentId;
    }
    return profile?.department_id;
  }, [selectedDepartmentId, profile?.department_id]);

  // 簡報模式
  const scrollContainerRef = useRef(null);
  const scrollThrottleRef = useRef(null);
  const publishTimerRef = useRef(null);
  // 目前的畫面狀態（報告人同步出去、主持人暫存自己的畫面都用這份）
  const uiStateRef = useRef({});
  // 主持人切去看別人之前，先記住自己整理好的畫面（隱藏、展開），切回自己時還原
  const ownPrepRef = useRef(null);
  // 主持人：報告人最新的畫面狀態 { memberId, state }，以及資料版本 { memberId, version }
  const presenterSnapshotRef = useRef(null);
  const presenterVersionRef = useRef(null);
  // 主持人：要等內容畫出來才能套用的捲動位置
  const pendingScrollRef = useRef(null);
  // 載入中的成員：這段期間收到的畫面狀態要等載入完再套，不然會被預設值蓋掉
  const loadingMemberRef = useRef(null);
  const loadSeqRef = useRef(0);
  const loadMemberDataRef = useRef(null);
  const prevSelectedRef = useRef(null);
  // 主持人：最後一次載入、還沒依報告人清單過濾的全部專案。報告人的清單晚到時直接重排，不用重撈
  const loadedProjectsRef = useRef(null);

  useEffect(() => {
    uiStateRef.current = {
      activeTab,
      expandedTickets,
      expandedProjects,
      hiddenTicketIds,
      dataVersion,
      projectIds: memberProjects.map(p => p.id),
    };
  });

  const applyUiState = useCallback((s) => {
    if (!s) return;
    if (s.activeTab !== undefined) setActiveTab(s.activeTab);
    if (s.expandedTickets) setExpandedTickets(new Set(s.expandedTickets));
    if (s.expandedProjects) setExpandedProjects(new Set(s.expandedProjects));
    if (s.hiddenTicketIds) setHiddenTicketIds(new Set(s.hiddenTicketIds));
    if (s.scrollRatio !== undefined) pendingScrollRef.current = s.scrollRatio;
  }, []);

  const applyScrollRatio = (ratio) => {
    const el = scrollContainerRef.current;
    if (!el) return false;
    el.scrollTop = ratio * (el.scrollHeight - el.clientHeight);
    return true;
  };

  // 報告人：要同步給主持人的畫面狀態。資料不傳，主持人自己撈
  const getPresenterState = useCallback(() => {
    const s = uiStateRef.current;
    const el = scrollContainerRef.current;
    const maxScroll = el ? el.scrollHeight - el.clientHeight : 0;
    return {
      activeTab: s.activeTab,
      expandedTickets: [...(s.expandedTickets || [])],
      expandedProjects: [...(s.expandedProjects || [])],
      hiddenTicketIds: [...(s.hiddenTicketIds || [])],
      // 顯示哪些專案、什麼順序。主持人讀不到別人的「我的展示專案」設定
      // （project_profiles 只能讀自己那筆），所以由報告人帶過去
      projectIds: [...(s.projectIds || [])],
      dataVersion: s.dataVersion,
      scrollRatio: maxScroll > 0 ? el.scrollTop / maxScroll : 0,
    };
  }, []);

  // 主持人：收到報告人的完整畫面狀態
  const handlePresenterSnapshot = useCallback((state, fromUserId) => {
    presenterSnapshotRef.current = { memberId: fromUserId, state };
    const prev = presenterVersionRef.current;
    presenterVersionRef.current = { memberId: fromUserId, version: state.dataVersion };
    // 報告人重撈過資料 → 主持人也重撈，載入完會再套一次這份畫面狀態
    if (prev && prev.memberId === fromUserId && prev.version !== state.dataVersion) {
      loadMemberDataRef.current?.(fromUserId);
      return;
    }
    if (loadingMemberRef.current === fromUserId) return;
    // 資料已經載好了，但專案清單跟報告人的不一樣（清單比資料晚到）→ 照他的清單重排
    const loaded = loadedProjectsRef.current;
    if (loaded?.memberId === fromUserId && Array.isArray(state.projectIds)
      && state.projectIds.join(',') !== (uiStateRef.current.projectIds || []).join(',')) {
      setMemberProjects(state.projectIds.map(id => loaded.byId[id]).filter(Boolean));
    }
    applyUiState(state);
  }, [applyUiState]);

  // 主持人：收到報告人的捲動位置
  const handlePresenterScroll = useCallback((ratio) => {
    if (loadingMemberRef.current || !applyScrollRatio(ratio)) pendingScrollRef.current = ratio;
  }, []);

  const {
    connection,
    connectionSince,
    now,
    hosting,
    following,
    autoEnded,
    presenterTabId,
    presenterHosting,
    anotherTabHosting,
    presentingTo,
    presentingElsewhere,
    viewedBy,
    getMemberStatus,
    startHosting,
    endHosting,
    dismissAutoEnded,
    assignPresenter,
    setFollowing,
    publishState,
    publishScroll,
  } = useDailyReportSession({
    profile,
    departmentId: effectiveDepartmentId,
    getPresenterState,
    onPresenterSnapshot: handlePresenterSnapshot,
    onPresenterScroll: handlePresenterScroll,
  });

  // 目前顯示誰的內容：主持中是正在看的人（預設自己），平常只看得到自己
  const selectedMember = hosting?.presenterId || profile?.id || null;
  const isPresenting = presentingTo.length > 0;

  // 換人時：主持人離開自己的畫面前先記下來；上一位報告人的畫面狀態作廢
  useEffect(() => {
    const prev = prevSelectedRef.current;
    prevSelectedRef.current = selectedMember;
    if (!prev || prev === selectedMember) return;
    if (prev === profile?.id) {
      const s = uiStateRef.current;
      ownPrepRef.current = {
        activeTab: s.activeTab,
        expandedTickets: [...s.expandedTickets],
        expandedProjects: [...s.expandedProjects],
        hiddenTicketIds: [...s.hiddenTicketIds],
      };
    }
    presenterSnapshotRef.current = null;
    presenterVersionRef.current = null;
    pendingScrollRef.current = null;
  }, [selectedMember]);

  // 主持人：內容畫出來之後再套用捲動位置
  useEffect(() => {
    if (pendingScrollRef.current == null || loading) return undefined;
    const ratio = pendingScrollRef.current;
    const id = requestAnimationFrame(() => {
      if (applyScrollRatio(ratio)) pendingScrollRef.current = null;
    });
    return () => cancelAnimationFrame(id);
  });

  // 報告人：畫面狀態一變就同步給主持人（50ms 內的連續變化合併成一次）
  useEffect(() => {
    if (!isPresenting) return undefined;
    clearTimeout(publishTimerRef.current);
    publishTimerRef.current = setTimeout(() => publishState(), 50);
    return () => clearTimeout(publishTimerRef.current);
  }, [isPresenting, activeTab, expandedTickets, expandedProjects, hiddenTicketIds, dataVersion, publishState]);

  // 報告人：捲動時同步捲動比例（每 200ms 一次，停下來的位置也一定會送到）
  const handleContentScroll = useCallback((e) => {
    if (!isPresenting || scrollThrottleRef.current) return;
    const el = e.currentTarget;
    scrollThrottleRef.current = setTimeout(() => {
      scrollThrottleRef.current = null;
      const maxScroll = el.scrollHeight - el.clientHeight;
      publishScroll(maxScroll > 0 ? el.scrollTop / maxScroll : 0);
    }, 200);
  }, [isPresenting, publishScroll]);


  // 搜尋高亮
  const [highlightKeyword, setHighlightKeyword] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [totalMatches, setTotalMatches] = useState(0);
  const globalMatchCounterRef = useRef(0);

  // 預定義高亮顏色
  const PRESET_HIGHLIGHT_COLORS = [
    { background: '#ffeb3b', color: '#000' },
    { background: '#80cbc4', color: '#000' },
    { background: '#ce93d8', color: '#000' },
    { background: '#ffcc80', color: '#000' },
    { background: '#a5d6a7', color: '#000' },
  ];

  const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const generateColorFromHash = (text) => {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash % 360);
    const saturation = 65 + (Math.abs(hash) % 20);
    const lightness = 85 + (Math.abs(hash >> 8) % 10);
    return { background: `hsl(${hue}, ${saturation}%, ${lightness}%)`, color: '#000' };
  };

  const getHighlightStyle = (keywordIndex, keyword) => {
    if (keywordIndex < PRESET_HIGHLIGHT_COLORS.length) {
      return PRESET_HIGHLIGHT_COLORS[keywordIndex];
    }
    return generateColorFromHash(keyword);
  };

  const highlightText = (text, searchText) => {
    if (!searchText || !text) return text;
    const keywords = searchText.trim().split(/\s+/).filter(k => k.length > 0);
    if (keywords.length === 0) return text;

    const escapedKeywords = keywords.map(k => escapeRegExp(k));
    const pattern = new RegExp(`(${escapedKeywords.join('|')})`, 'gi');
    const parts = text.split(pattern);

    return (
      <>
        {parts.map((part, index) => {
          const keywordIndex = keywords.findIndex(k => k.toLowerCase() === part.toLowerCase());
          if (keywordIndex !== -1) {
            const style = getHighlightStyle(keywordIndex, keywords[keywordIndex]);
            const globalMatchIndex = globalMatchCounterRef.current++;
            const isCurrentMatch = globalMatchIndex === currentMatchIndex;
            return (
              <mark
                key={index}
                className={`highlight ${isCurrentMatch ? 'current-match' : ''}`}
                data-match-index={globalMatchIndex}
                style={{
                  backgroundColor: isCurrentMatch ? '#ff6b35' : style.background,
                  color: isCurrentMatch ? '#fff' : style.color,
                  padding: '2px 4px',
                  borderRadius: '2px',
                  fontWeight: 600,
                  transition: 'all 0.3s ease',
                  boxShadow: isCurrentMatch
                    ? '0 0 0 3px rgba(255, 107, 53, 0.3), 0 0 12px rgba(255, 107, 53, 0.6)'
                    : 'none',
                  outline: isCurrentMatch ? '2px solid #ff6b35' : 'none',
                  outlineOffset: '2px',
                }}
              >
                {part}
              </mark>
            );
          }
          return <span key={index}>{part}</span>;
        })}
      </>
    );
  };

  const countHighlightMatches = () => {
    if (!highlightKeyword || memberProjects.length === 0) return 0;
    const keywords = highlightKeyword.trim().split(/\s+/).filter(k => k.length > 0);
    if (keywords.length === 0) return 0;

    let count = 0;
    memberProjects.forEach(project => {
      keywords.forEach(keyword => {
        const regex = new RegExp(escapeRegExp(keyword), 'gi');
        const nameMatches = (project.name || '').match(regex);
        if (nameMatches) count += nameMatches.length;
        const descMatches = (project.description || '').match(regex);
        if (descMatches) count += descMatches.length;
      });
      project.tickets.forEach(ticket => {
        keywords.forEach(keyword => {
          const regex = new RegExp(escapeRegExp(keyword), 'gi');
          const titleMatches = (ticket.title || '').match(regex);
          if (titleMatches) count += titleMatches.length;
        });
      });
    });
    return count;
  };

  useEffect(() => {
    const count = countHighlightMatches();
    setTotalMatches(count);
    setCurrentMatchIndex(0);
  }, [highlightKeyword, memberProjects]);

  const scrollToMatch = (matchIndex) => {
    if (totalMatches === 0) return;
    const allHighlights = document.querySelectorAll('.highlight[data-match-index]');
    const target = allHighlights[matchIndex];
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && totalMatches > 0) {
      e.preventDefault();
      let nextIndex;
      if (e.shiftKey) {
        nextIndex = currentMatchIndex > 0 ? currentMatchIndex - 1 : totalMatches - 1;
      } else {
        nextIndex = currentMatchIndex < totalMatches - 1 ? currentMatchIndex + 1 : 0;
      }
      setCurrentMatchIndex(nextIndex);
      setTimeout(() => scrollToMatch(nextIndex), 100);
    }
  };

  const goToPreviousMatch = () => {
    if (totalMatches === 0) return;
    const prevIndex = currentMatchIndex > 0 ? currentMatchIndex - 1 : totalMatches - 1;
    setCurrentMatchIndex(prevIndex);
    setTimeout(() => scrollToMatch(prevIndex), 100);
  };

  const goToNextMatch = () => {
    if (totalMatches === 0) return;
    const nextIndex = currentMatchIndex < totalMatches - 1 ? currentMatchIndex + 1 : 0;
    setCurrentMatchIndex(nextIndex);
    setTimeout(() => scrollToMatch(nextIndex), 100);
  };

  // === 部門列表來自 BetaDataContext，自動設定預設部門（記住上次選擇）===
  useEffect(() => {
    if ((isCompanyAdmin() || isSuperAdmin()) && departments.length > 0 && !selectedDepartmentId) {
      // 優先讀取上次儲存的 DailyReport 檢視部門
      const savedDeptId = profile?.settings?.daily_report_viewing_department_id;
      const savedDept = savedDeptId && departments.find(d => d.id === savedDeptId);
      const defaultDept = savedDept || departments.find(d => d.id === profile?.department_id) || departments[0];
      setSelectedDepartmentId(defaultDept.id);
    }
  }, [departments]);

  // === 部門切換時儲存到 profile.settings（super_admin / company_admin）===
  const saveDepartmentSelection = useCallback(async (deptId) => {
    if (!profile?.id || (!isSuperAdmin() && !isCompanyAdmin())) return;
    const newSettings = {
      ...(profile?.settings || {}),
      daily_report_viewing_department_id: deptId,
    };
    await updateUserSettings(profile.id, newSettings);
  }, [profile?.id, profile?.settings]);

  // === 載入成員（部門變更時）===
  // 成員名單只用在主持時的人頭列；自己的內容不受部門切換影響
  useEffect(() => {
    if (effectiveDepartmentId) {
      loadMembers(effectiveDepartmentId);
    }
  }, [effectiveDepartmentId]);

  const loadMembers = async (departmentId) => {
    setMembersLoading(true);
    const { data: visibleMemberIds, error } = await getDailyReportVisibleMembers(departmentId);

    if (error) {
      console.error('載入顯示設定失敗:', error);
    }

    if (!visibleMemberIds || visibleMemberIds.length === 0) {
      setMembers([]);
      setMembersLoading(false);
      return;
    }

    // 用 companyUsers 做 client-side join，按 visibleMemberIds 順序排列
    const visibleMembers = visibleMemberIds
      .map(id => companyUsers.find(u => u.id === id))
      .filter(m => m);

    setMembers(visibleMembers);
    setMembersLoading(false);
  };

  // === 載入成員的 tickets ===
  // 主持人看報告人時也是自己撈資料，報告人只同步畫面狀態
  useEffect(() => {
    if (selectedMember) {
      loadMemberData(selectedMember);
    } else {
      setMemberProjects([]);
    }
  }, [selectedMember]);

  const loadMemberData = async (memberId) => {
    // 主持人連續換人時，只採用最後一次的結果
    const seq = ++loadSeqRef.current;
    loadingMemberRef.current = memberId;
    setLoading(true);
    try {
      // 1. 並行：今日 unassign(ticket + work_item) + 專案顯示設定 + 交付項目(引退中,暫留撈取)
      const [
        { data: unassignedIds }, { data: unassignedWiIds },
        { data: projectProfile }, { data: deliverableItems },
      ] = await Promise.all([
        getTodayUnassignedTicketIds(memberId),
        getTodayUnassignedWorkItemIds(memberId),
        getProjectProfile(memberId),
        getDeliverableItemsForUser(memberId, { companyId: viewingCompanyId }),
      ]);
      const visibleProjectIds = projectProfile?.settings?.daily_report_visible_project_ids;

      // 2. 查詢 tickets + work_items（各自依賴自己的 unassignedIds）
      const [{ data: tickets, error: tErr }, { data: workItems, error: wErr }] = await Promise.all([
        getTicketsForDailyReport(memberId, {
          companyId: viewingCompanyId,
          todayUnassignedTicketIds: unassignedIds || [],
        }),
        getWorkItemsForDailyReport(memberId, {
          companyId: viewingCompanyId,
          todayUnassignedWorkItemIds: unassignedWiIds || [],
        }),
      ]);
      if (tErr) throw tErr;
      if (wErr) throw wErr;
      if (seq !== loadSeqRef.current) return; // 已經換人了，這份結果作廢

      // 4. 按專案分群（ticket + work_item 混在同一專案）
      const projectMap = {};
      const ensureProject = (project) => {
        if (!projectMap[project.id]) {
          projectMap[project.id] = {
            id: project.id,
            name: project.name,
            color: project.color,
            icon: project.icon,
            description: project.description,
            externalUrl: project.external_url,
            tickets: [],
            workItems: [],
          };
        }
        return projectMap[project.id];
      };
      (tickets || []).forEach(ticket => {
        ensureProject(ticket.projects || { id: ticket.project_id, name: '未分類' }).tickets.push(ticket);
      });
      // work_item 建父子結構:子項掛到母項底下,不要跟母項平排(平排會讓子項夾在
      // 別的母項中間,而且看不出屬於誰)。母項沒指派給我時 API 會補 _context 母項。
      const wiById = new Map((workItems || []).map(w => [w.id, { ...w, children: [] }]));
      wiById.forEach(node => {
        if (node.parent_id && wiById.has(node.parent_id)) wiById.get(node.parent_id).children.push(node);
      });
      wiById.forEach(node => {
        if (node.children.length > 1) node.children.sort(compareWorkItemSiblings);
      });
      wiById.forEach(node => {
        if (node.parent_id && wiById.has(node.parent_id)) return; // 子項由母項帶出
        ensureProject(node.projects || { id: node.project_id, name: '未分類' }).workItems.push(node);
      });
      // 同群組的工作項目聚在一起(日報要按群組唸)。群組順序用 DB 的 sort_order,
      // 不用工作項目列表的個人排序 —— 否則同一份報告每個人看到的順序不同,報告時對不上。
      // 未分組一律排最後。sort 是穩定排序,所以群組內維持原本的編號遞減。
      Object.values(projectMap).forEach(p => {
        p.workItems.sort(compareByGroup);
      });

      // 5. 依使用者設定排序/過濾
      // visibleProjectIds 存在（含空陣列）= 使用者有設定過，尊重選擇
      // visibleProjectIds 不存在（undefined/null）= 從未設定，顯示全部
      // 主持人看別人時讀不到他的設定（project_profiles 只能讀自己那筆），
      // 改用報告人同步過來的專案清單；他離線、沒有清單時才全部顯示
      const snapshot = presenterSnapshotRef.current;
      const presenterProjectIds = snapshot?.memberId === memberId ? snapshot.state.projectIds : null;
      const orderIds = Array.isArray(visibleProjectIds) ? visibleProjectIds
        : (Array.isArray(presenterProjectIds) ? presenterProjectIds : null);
      loadedProjectsRef.current = { memberId, byId: projectMap };
      let result;
      if (orderIds) {
        result = orderIds
          .map(id => projectMap[id])
          .filter(Boolean);
      } else {
        result = Object.values(projectMap);
      }

      setMemberDeliverableItems(deliverableItems || []);
      setMemberProjects(result);
      // 畫面狀態：主持人看報告人 → 套報告人的；主持人切回自己 → 還原自己整理好的；其餘用預設
      if (snapshot && snapshot.memberId === memberId) {
        applyUiState(snapshot.state);
      } else if (memberId === profile?.id && ownPrepRef.current) {
        applyUiState(ownPrepRef.current);
        ownPrepRef.current = null;
      } else {
        setExpandedProjects(new Set(result.map(p => p.id)));
        setActiveTab(result.length > 0 ? 'all' : ((deliverableItems || []).length > 0 && showDeliverables ? 'deliverables' : 'all'));
        setHiddenTicketIds(new Set());
      }
      setShowHiddenList(false);
      setDataVersion(v => v + 1);
    } catch (error) {
      if (seq !== loadSeqRef.current) return;
      console.error('載入成員資料失敗:', error);
      toast.error('載入失敗');
    }
    if (seq !== loadSeqRef.current) return;
    loadingMemberRef.current = null;
    setLoading(false);
  };

  // 主持人收到「報告人重撈了資料」時要呼叫最新的 loadMemberData（裡面會用到 viewingCompanyId）
  useEffect(() => { loadMemberDataRef.current = loadMemberData; });

  // === 關閉 DailyReport ===
  // 主持或報告中：點灰底不會關，按 ✕ 先確認
  const handleClose = useCallback(async () => {
    if (hosting) {
      const ok = await confirm({
        title: '結束簡報',
        message: '關閉 DailyReport 會結束這場簡報，確定要關閉嗎？',
        confirmText: '結束並關閉',
      });
      if (!ok) return;
      endHosting();
    } else if (isPresenting) {
      const ok = await confirm({
        title: '你正在報告中',
        message: '關閉之後，主持人的畫面就不會再同步你的操作。確定要關閉嗎？',
        confirmText: '關閉',
        type: 'warning',
      });
      if (!ok) return;
    }
    onClose();
  }, [hosting, isPresenting, confirm, endHosting, onClose]);

  // 正在幫別人報告時按「開始簡報」→ 先確認，開始後那位主持人就不會再同步你的畫面
  const handleStartHosting = useCallback(async () => {
    if (isPresenting) {
      const hosts = presentingTo.map(a => a.hostName).join('、');
      const ok = await confirm({
        title: '你正在報告中',
        message: `你正在向 ${hosts} 報告，開始自己的簡報後，${hosts} 就不會再同步你的畫面。確定要開始嗎？`,
        confirmText: '開始簡報',
        type: 'warning',
      });
      if (!ok) return;
    }
    startHosting();
  }, [isPresenting, presentingTo, confirm, startHosting]);

  const handleOverlayClick = useCallback(() => {
    if (hosting || isPresenting) return;
    onClose();
  }, [hosting, isPresenting, onClose]);

  // === 展開/收合 ===
  const toggleTicketExpand = (ticketId) => {
    setExpandedTickets(prev => {
      const next = new Set(prev);
      if (next.has(ticketId)) next.delete(ticketId);
      else next.add(ticketId);
      return next;
    });
  };

  const toggleProjectExpand = (projectId) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  // === 隱藏 Ticket ===
  const toggleTicketHidden = (ticketId) => {
    setHiddenTicketIds(prev => {
      const next = new Set(prev);
      if (next.has(ticketId)) next.delete(ticketId);
      else next.add(ticketId);
      return next;
    });
  };

  // === Ticket 編輯 ===
  const handleEditTicket = (ticket) => {
    onNavigate('ticket', null, ticket.id, { ticketNo: ticket.ticket_no });
  };

  // === work_item 編輯 ===(切到新版 UI 並用 ?wi= 開 drawer,由 BetaLayout handleNavigate 處理)
  // 子項不走完整內頁:開母項 drawer 並帶子項 id,由 WorkItemApp 彈出該子項的輕量 card
  const handleEditWorkItem = (wi) => {
    if (wi.parent_id) {
      onNavigate('work_item', null, wi.parent_id, { subId: wi.id });
    } else {
      onNavigate('work_item', null, wi.id);
    }
  };

  // === 重新整理 ===
  const handleRefresh = async () => {
    if (!selectedMember) return;
    await loadMemberData(selectedMember);
  };

  // === Status helpers ===
  const getStatusLabel = (status) => {
    const opt = statusOptions.find(o => o.value === status);
    return opt?.label || status || '待處理';
  };

  const getStatusColor = (status) => {
    const opt = statusOptions.find(o => o.value === status);
    return opt?.color || '#999';
  };

  // === Owner 解析 ===
  // ticket_assignees 已依 assigned_at 升冪排序，[0] 即當前 Owner（與詳情頁一致）
  const resolveTicketOwner = (ticket) => {
    const ownerAssignee = ticket.ticket_assignees?.[0];
    if (!ownerAssignee) return null;
    const email = ownerAssignee.profiles?.email?.toLowerCase();
    const user = (email && usersMapByEmail[email])
      || companyUsers.find(u => u.id === ownerAssignee.user_id)
      || null;
    return {
      avatarUrl: user?.avatar_url,
      name: user?.name || ownerAssignee.profiles?.email || '未知',
    };
  };

  // 預估 / 實際工時。母項通常不填(時數在子項上),沒有就不顯示這個 chip。
  // 格式對齊交付項目區塊的 di-hours。
  const hoursLabel = (wi) => {
    const e = Number(wi.estimated_hours) || 0;
    const a = Number(wi.actual_hours) || 0;
    if (!e && !a) return null;
    return `預估 ${fmtHours(e)}h / 實際 ${fmtHours(a)}h`;
  };

  // 單一工作項目(母項)的列。抽成函式讓群組分隔列可以插在它們之間。
  const renderWorkItemRow = (wi) => {
    const owner = resolveWorkItemOwner(wi);
    const wiKey = `wi-${wi.id}`;
    // _context = 母項沒指派給我,只是拿來當子項的脈絡標題:
    // 不給編輯/隱藏、不算進統計,狀態與時數也不顯示(那不是我的工作)
    const isCtx = !!wi._context;
    return (
    <div
      key={wiKey}
      className={`task-item ${expandedTickets.has(wiKey) ? 'expanded' : ''}${isCtx ? ' context-only' : ''}`}
    >
      <div className="task-header" onClick={() => toggleTicketExpand(wiKey)}>
        <span className="expand-icon">
          {wi.description ? (expandedTickets.has(wiKey) ? '▼' : '▶') : ''}
        </span>
        <div className="task-main">
          <div
            className="task-status-indicator"
            style={{ background: getStatusColor(wi.status) }}
          />
          <div className="task-info">
            <h5 className="task-title">
              <span className="ticket-no-badge wi-badge">
                工作項目{wi.work_item_no ? ` #${wi.work_item_no}` : ''}
              </span>
              {highlightText(wi.title, highlightKeyword)}
              {isCtx && <span className="wi-context-tag" title="未指派給你,僅作為子項的母項脈絡">脈絡</span>}
            </h5>
          </div>
        </div>
        <div className="task-meta">
          {!isCtx && owner && (
            <span className="task-owner" title={`Owner：${owner.name}`}>
              <Avatar avatarUrl={owner.avatarUrl} name={owner.name} size="xsmall" />
              <span className="task-owner-name">{owner.name}</span>
            </span>
          )}
          {!isCtx && (
            <span
              className="task-status"
              style={{ borderColor: getStatusColor(wi.status), color: getStatusColor(wi.status) }}
            >
              {getStatusLabel(wi.status)}
            </span>
          )}
          {/* 有子項 → 顯示子項時數總和(時數通常填在子項上,母項自己多半沒填);
              沒子項才顯示自身。脈絡母項不是我的工作,但底下子項是,所以總和照顯示。*/}
          {(() => {
            // 有子項 → 算子項總和(時數通常填在子項上,母項自己多半沒填);
            // 沒子項 → 用自己。文案與專案標題列一致。
            const src = (wi.children || []).length > 0 ? wi.children : (isCtx ? [] : [wi]);
            const label = hoursSummary(src);
            return label ? <span className="task-hours">{label}</span> : null;
          })()}
          {!isCtx && wi.start_date && wi.end_date && (
            <span className="task-dates">{wi.start_date} ~ {wi.end_date}</span>
          )}
        </div>
        {!isCtx && (
          <>
            <button
              className="edit-task-btn"
              onClick={(e) => { e.stopPropagation(); handleEditWorkItem(wi); }}
              title="編輯工作項目"
            >
              ✏️
            </button>
            <button
              className="hide-task-btn"
              onClick={(e) => { e.stopPropagation(); toggleTicketHidden(wiKey); }}
              title="從報告中隱藏此工作項目"
            >
              隱藏
            </button>
          </>
        )}
      </div>
      {expandedTickets.has(wiKey) && wi.description && (
        <div className="task-details">
          <div className="task-description-full">
            <div className="description-label">工作項目說明：</div>
            <div className="description-content">
              <MarkdownViewer content={wi.description} linkTickets={false} />
            </div>
          </div>
        </div>
      )}
      {/* 子項:收在母項底下的窄列。層級較低所以行高與字級都縮一階,
          但展開說明 / 狀態 / 編輯 / 隱藏一律保留。
          順序由 compareWorkItemSiblings 決定,跟 Drawer 的子項 tab 一致。*/}
      {(wi.children || []).length > 0 && (
        <div className="subtask-list">
          {wi.children.map(sub => {
            const subKey = `wi-${sub.id}`;
            const subOwner = resolveWorkItemOwner(sub);
            return (
              <div key={subKey} className={`subtask-item ${expandedTickets.has(subKey) ? 'expanded' : ''}`}>
                <div className="subtask-header" onClick={() => toggleTicketExpand(subKey)}>
                  <span className="subtask-connector" />
                  <span className="expand-icon">
                    {sub.description ? (expandedTickets.has(subKey) ? '▼' : '▶') : ''}
                  </span>
                  <span
                    className="subtask-status-indicator"
                    style={{ background: getStatusColor(sub.status) }}
                  />
                  <span className="subtask-title">
                    {highlightText(sub.title, highlightKeyword)}
                  </span>
                  <div className="subtask-meta">
                    {subOwner && (
                      <span className="task-owner" title={`Owner：${subOwner.name}`}>
                        <Avatar avatarUrl={subOwner.avatarUrl} name={subOwner.name} size="xsmall" />
                      </span>
                    )}
                    <span
                      className="task-status"
                      style={{ borderColor: getStatusColor(sub.status), color: getStatusColor(sub.status) }}
                    >
                      {getStatusLabel(sub.status)}
                    </span>
                    {hoursLabel(sub) && <span className="task-hours">{hoursLabel(sub)}</span>}
                    {sub.start_date && sub.end_date && (
                      <span className="task-dates">{sub.start_date} ~ {sub.end_date}</span>
                    )}
                  </div>
                  <button
                    className="edit-task-btn"
                    onClick={(e) => { e.stopPropagation(); handleEditWorkItem(sub); }}
                    title="編輯子項"
                  >
                    ✏️
                  </button>
                  <button
                    className="hide-task-btn"
                    onClick={(e) => { e.stopPropagation(); toggleTicketHidden(subKey); }}
                    title="從報告中隱藏此子項"
                  >
                    隱藏
                  </button>
                </div>
                {expandedTickets.has(subKey) && sub.description && (
                  <div className="task-details">
                    <div className="task-description-full">
                      <div className="description-label">子項說明：</div>
                      <div className="description-content">
                        <MarkdownViewer content={sub.description} linkTickets={false} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
    );
  };

  // work_item 的 Owner:assignees 已依 assigned_at 升冪,[0] 即 Owner;只有 user_id,靠 companyUsers 解析
  const resolveWorkItemOwner = (wi) => {
    const ownerA = wi.assignees?.[0];
    if (!ownerA) return null;
    const user = companyUsers.find(u => u.id === ownerA.user_id) || null;
    return { avatarUrl: user?.avatar_url, name: user?.name || '未知' };
  };

  // === 統計 ===
  // 自己不一定在簡報成員名單裡，找不到就從公司成員裡找
  const selectedMemberData = members.find(m => m.id === selectedMember)
    || companyUsers.find(u => u.id === selectedMember)
    || (selectedMember === profile?.id ? profile : null);
  const totalTickets = memberProjects.reduce((sum, p) => sum + p.tickets.length, 0);
  const completedTickets = memberProjects.reduce(
    (sum, p) => sum + p.tickets.filter(t => t.status === 'completed' || t.status === 'closed').length,
    0
  );
  // 統計:子項是實際工作單位所以要算;_context 母項只是脈絡標題,不算。
  const flattenWorkItems = (list) => (list || []).flatMap(w => [w, ...(w.children || [])]).filter(w => !w._context);
  // ── 時數加總 ──
  // 剩餘的定義刻意不綁 status(否則得逐一討論哪些 status 算完成),只看兩件事:
  //   1. is_completed —— 就是畫面上那個勾選框,勾了剩餘一律是 0
  //   2. 預估 − 實際 —— WIP 自然被扣掉(估 6h、已投入 2h → 剩 4h)
  // 超支不給負數。沒人更新「實際」時 WIP 會被當成全額預估,那是保守估計,
  // 不會低估剩餘工作量。
  const sumHours = (list) => (list || []).reduce((acc, w) => {
    const est = Number(w.estimated_hours) || 0;
    const act = Number(w.actual_hours) || 0;
    return {
      estimated: acc.estimated + est,
      actual: acc.actual + act,
      remaining: acc.remaining + (w.is_completed ? 0 : Math.max(0, est - act)),
    };
  }, { estimated: 0, actual: 0, remaining: 0 });
  // 小數點只在需要時出現(7 而不是 7.0;7.5 保留)
  const fmtHours = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
  const hoursSummary = (list) => {
    const { estimated, actual, remaining } = sumHours(list);
    if (!estimated && !actual) return null;
    return `預估 ${fmtHours(estimated)}h / 實際 ${fmtHours(actual)}h / 剩餘 ${fmtHours(remaining)}h`;
  };

  const totalWorkItems = memberProjects.reduce((sum, p) => sum + flattenWorkItems(p.workItems).length, 0);
  const completedWorkItems = memberProjects.reduce(
    (sum, p) => sum + flattenWorkItems(p.workItems).filter(w => w.status === 'completed' || w.status === 'closed').length,
    0
  );

  // Tab 過濾 + 隱藏 ticket
  const filteredProjects = useMemo(() => {
    let projects;
    if (activeTab === 'deliverables') return [];
    if (activeTab === 'all') projects = memberProjects;
    else projects = memberProjects.filter(p => p.id === activeTab);

    if (hiddenTicketIds.size === 0) return projects;
    return projects.map(p => ({
      ...p,
      tickets: p.tickets.filter(t => !hiddenTicketIds.has(t.id)),
      // 母項被隱藏 → 整組(含子項)一起消失;母項留著 → 只濾掉個別被隱藏的子項
      workItems: (p.workItems || [])
        .filter(w => !hiddenTicketIds.has(`wi-${w.id}`))
        .map(w => ({ ...w, children: (w.children || []).filter(c => !hiddenTicketIds.has(`wi-${c.id}`)) })),
    })).filter(p => p.tickets.length > 0 || p.workItems.length > 0);
  }, [activeTab, memberProjects, hiddenTicketIds]);

  // 所有 tab 都可能有交付項目（專案 tab 顯示該專案的，全部/交付項目顯示全部）
  // 交付項目(Phase 體系)引退中 → 先隱藏不顯示於 DailyReport;code 全保留,改回 true 即恢復
  const showDeliverables = false;

  // 依 tab 過濾交付項目：專案 tab 只顯示該專案的，全部/交付項目 tab 顯示全部
  const filteredDeliverableItems = useMemo(() => {
    if (activeTab === 'all' || activeTab === 'deliverables') return memberDeliverableItems;
    // 特定專案 tab → 只顯示該專案的交付項目
    return memberDeliverableItems.filter(item => {
      const phase = item.deliverables?.phases || {};
      return phase.project_id === activeTab;
    });
  }, [activeTab, memberDeliverableItems]);

  // 每次渲染前重置全域計數器
  globalMatchCounterRef.current = 0;

  const showDeptSelector = isCompanyAdmin() || isSuperAdmin();
  const canManageMembers = ['super_admin', 'company_admin', 'department_admin'].includes(profile?.role);

  // ── 簡報狀態：放在標題列「開始簡報」左邊，有狀況就一直顯示到解決為止，不會像 toast 一樣自己消失 ──
  // text 是膠囊上的短字；hint 是補充說明，放在滑鼠提示裡（空間不夠時膠囊會截斷，提示裡看得到全文）
  const personName = (id) => members.find(m => m.id === id)?.name
    || companyUsers.find(u => u.id === id)?.name
    || '對方';
  const statusBar = (() => {
    const isConnecting = connection === 'connecting' || connection === 'reconnecting';
    if (isConnecting && now - connectionSince >= RECONNECT_GIVE_UP_MS) {
      return {
        tone: 'error',
        text: '連不上簡報連線',
        hint: '已經超過 1 分鐘連不上，請重新整理頁面',
        action: { label: '重新整理', onClick: () => window.location.reload() },
      };
    }
    if (connection === 'reconnecting') {
      return {
        tone: 'warn',
        text: '連線中斷，重新連線中…',
        hint: (hosting || isPresenting) ? '接回後簡報會自動恢復，不用重新整理' : null,
      };
    }
    if (connection === 'connecting' && now - connectionSince >= 3000) {
      return { tone: 'warn', text: '連線中…' };
    }
    if (hosting) {
      const pid = hosting.presenterId;
      if (!pid) return { tone: 'live', text: '簡報中 · 點下方頭像交給他報告' };
      const name = personName(pid);
      const status = getMemberStatus(pid);
      if (status === 'offline') {
        return { tone: 'warn', text: `${name} 離線中，畫面不會同步`, hint: '他打開 DailyReport 後會自動接上' };
      }
      // 只想看看、不想被他的捲動影響 → 停止同步之後就是自己看；再點一次他的頭像或按「恢復同步」
      if (!following) {
        return {
          tone: 'info',
          text: `正在看 ${name} 的內容`,
          hint: '沒有同步他的畫面，他的捲動不會影響你',
          action: { label: '恢復同步', onClick: () => setFollowing(true) },
        };
      }
      // 他自己也按了「開始簡報」→ 他現在是主持人，不會再同步給你；等他結束自己的簡報就會自動接回
      if (presenterHosting) {
        return {
          tone: 'warn',
          text: `${name} 開始了自己的簡報，畫面已停止同步`,
          hint: '等他結束自己的簡報，就會自動接回同步',
        };
      }
      if (!presenterTabId) {
        return now - hosting.assignedAt < PRESENTER_NO_RESPONSE_MS
          ? { tone: 'live', text: `等待 ${name} 的畫面…` }
          : { tone: 'warn', text: `${name} 沒有回應`, hint: '他可能不在頁面上，你可以自己操作畫面' };
      }
      const stopSync = { label: '停止同步', onClick: () => setFollowing(false) };
      return status === 'idle'
        ? { tone: 'live', text: `${name} 報告中（閒置）`, hint: '他目前閒置中，你可以自己操作畫面', action: stopSync }
        : { tone: 'live', text: `${name} 報告中`, hint: '只是想看看他的內容的話，按「停止同步」就不會被他的捲動影響', action: stopSync };
    }
    if (autoEnded) {
      return {
        tone: 'info',
        text: '簡報已自動結束',
        hint: `${HOST_IDLE_END_MS / 60000} 分鐘沒有任何操作，所以自動結束了`,
        action: { label: '知道了', onClick: dismissAutoEnded },
      };
    }
    if (anotherTabHosting) {
      return { tone: 'info', text: '簡報在你的另一個分頁', action: { label: '在這裡接手', onClick: handleStartHosting } };
    }
    if (isPresenting) {
      const hosts = presentingTo.map(a => a.hostName).join('、');
      return { tone: 'live', text: `你正在報告中 · ${hosts}`, hint: `你的畫面正同步給 ${hosts}` };
    }
    if (presentingElsewhere) return { tone: 'info', text: '你在另一個分頁報告中' };
    if (viewedBy.length > 0) {
      const viewers = viewedBy.map(a => a.hostName).join('、');
      return { tone: 'info', text: `${viewers} 正在看你的內容`, hint: '只是在看，沒有同步你的畫面' };
    }
    return null;
  })();

  // 主持中的人頭列：點自己 = 看自己的內容；點別人 = 交給他報告（他離線也看得到內容，只是不同步）
  const handleRosterClick = (memberId) => assignPresenter(memberId === profile?.id ? null : memberId);
  // 頭像太多時左右捲動；一般滑鼠只有上下滾輪，把上下滾換成左右捲
  const handleRosterWheel = (e) => {
    const el = e.currentTarget;
    if (el.scrollWidth <= el.clientWidth || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
    el.scrollLeft += e.deltaY;
  };
  const rosterTitle = (member, status) => {
    const name = member.name || member.email || '';
    if (member.id === profile?.id) return '看自己的內容';
    if (member.id === hosting?.presenterId && !following && status !== 'offline') return `再點一次，恢復同步 ${name} 的畫面`;
    if (status === 'offline') return `${name} 離線中：可以看他的內容，但畫面不會同步`;
    if (status === 'idle') return `${name} 閒置中：點了照樣交給他報告，會帶出他準備好的畫面`;
    return `交給 ${name} 報告`;
  };

  return (
    <div className="daily-report-modal-overlay" onClick={handleOverlayClick}>
      <div className="daily-report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="daily-report-view">
          <div className="daily-report-header">
            <div className="header-title">
              <h2>
                {new Date().toLocaleDateString('zh-TW', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                }).replace(/\//g, '/')}
              </h2>
            </div>

            {/* 部門選擇器（公司管理員+）。主持中不能換：部門決定簡報在哪個 lobby */}
            {showDeptSelector && (
              <div className="department-selector">
                <label className="dept-label">部門：</label>
                {hosting ? (
                  <span className="dept-locked" title="簡報中無法切換部門">
                    {departments.find(d => d.id === selectedDepartmentId)?.name || ''}
                  </span>
                ) : departments.length > 0 ? (
                  <CustomSelect
                    value={selectedDepartmentId || ''}
                    onChange={(val) => { setSelectedDepartmentId(val); saveDepartmentSelection(val); }}
                    options={departments.map(dept => ({ value: dept.id, label: dept.name }))}
                    placeholder="選擇部門..."
                    searchable={departments.length > 5}
                    className="dept-custom-select"
                  />
                ) : (
                  <span className="dept-loading">載入中...</span>
                )}
              </div>
            )}

            {/* 設定自己在 Daily Report 展示哪些專案。原本掛在自己的成員頭像上，
                現在人頭列只有主持時才出現，所以改放這裡 */}
            <button
              className="my-projects-btn"
              onClick={() => setShowProjectSelector(true)}
              title="設定我在 Daily Report 中展示的專案"
            >
              ⚙️ 我的展示專案
            </button>

            {/* 簡報狀態：佔掉標題列剩下的空間、靠右貼著「開始簡報」。
                flex-basis 為 0，所以不會因為字太長把工具列擠到下一行，空間不夠就截斷 */}
            {statusBar && (
              <div className="session-status-slot">
                <div
                  className={`session-status-pill tone-${statusBar.tone}`}
                  role="status"
                  title={statusBar.hint ? `${statusBar.text}：${statusBar.hint}` : statusBar.text}
                >
                  <span className="status-text">{statusBar.text}</span>
                  {statusBar.action && (
                    <button className="status-action-btn" onClick={statusBar.action.onClick}>
                      {statusBar.action.label}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* 右上角工具列：放在標題列裡，跟標題一起垂直置中，標題列多高都對得齊 */}
            <div className="modal-top-toolbar">
              {/* 簡報按鈕放最前面，緊貼左邊的簡報狀態。開始之後下方會出現成員頭像，點誰就交給誰報告 */}
              <button
                className={`broadcast-toggle-btn ${hosting ? 'active' : ''}`}
                onClick={hosting ? endHosting : handleStartHosting}
                disabled={!hosting && !effectiveDepartmentId}
                title={hosting ? '結束這場簡報' : '開始簡報：點成員頭像就能請他報告，並同步他的畫面'}
              >
                <span className="btn-text">{hosting ? '結束簡報' : '開始簡報'}</span>
              </button>

              {/* 搜尋高亮輸入框 */}
              <div className="search-highlight-container">
                <div className="search-highlight-box">
                  <input
                    type="text"
                    placeholder="輸入關鍵字高亮顯示..."
                    value={highlightKeyword}
                    onChange={(e) => setHighlightKeyword(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    className="search-highlight-input"
                  />
                  {highlightKeyword && (
                    <>
                      <button
                        className="clear-search-btn"
                        onClick={() => setHighlightKeyword('')}
                        title="清除搜尋"
                      >
                        ✕
                      </button>
                      {totalMatches > 0 && (
                        <div className="search-navigation">
                          <button className="nav-btn" onClick={goToPreviousMatch} title="上一個 (Shift+Enter)">
                            ▲
                          </button>
                          <button className="nav-btn" onClick={goToNextMatch} title="下一個 (Enter)">
                            ▼
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
                {highlightKeyword && totalMatches > 0 && (
                  <div className="match-count-hint">
                    第 {currentMatchIndex + 1} / {totalMatches} 個結果
                  </div>
                )}
                {highlightKeyword && totalMatches === 0 && (
                  <div className="match-count-hint no-results">找不到符合的結果</div>
                )}
              </div>

              {/* 重新整理按鈕 */}
              <button
                className={`modal-refresh-btn ${loading ? 'is-loading' : ''}`}
                onClick={handleRefresh}
                disabled={!selectedMember || loading}
                title="重新載入資料"
                aria-label="重新載入資料"
              >
                <SvgIcon svg={refreshSvg} className="refresh-icon" />
              </button>

              {/* 關閉按鈕 */}
              <button className="modal-close-btn" onClick={handleClose} title="關閉">
                ✕
              </button>
            </div>
          </div>

          {/* 人頭列：只有主持人看得到，順序照「管理成員」的設定固定 */}
          {hosting && (
          <div className="member-selector">
            <div className="member-list" onWheel={handleRosterWheel}>
              {membersLoading ? (
                <div className="member-skeleton-row">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="member-skeleton">
                      <div className="skeleton skeleton-circle" />
                      <div className="skeleton skeleton-text" />
                    </div>
                  ))}
                </div>
              ) : members.length === 0 ? (
                <div className="no-members-hint">
                  {canManageMembers ? '還沒有簡報成員' : '管理員尚未設定此部門的簡報成員'}
                </div>
              ) : (
                members.map(member => {
                  const status = getMemberStatus(member.id);
                  const isSelf = member.id === profile?.id;
                  const isViewing = selectedMember === member.id;
                  const isSyncing = isViewing && !isSelf && following && !!presenterTabId;
                  return (
                    <button
                      key={member.id}
                      className={`member-btn status-${status} ${isViewing ? 'active' : ''}`}
                      onClick={() => handleRosterClick(member.id)}
                      title={rosterTitle(member, status)}
                    >
                      <span className="member-avatar-wrap">
                        <Avatar
                          avatarUrl={member.avatar_url}
                          name={member.name}
                          size="xsmall"
                          className="member-avatar"
                        />
                        <span className={`member-status-dot ${status}`} />
                      </span>
                      <span className="member-name">{member.name || member.email || '未命名'}</span>
                      {!isSelf && status !== 'online' && (
                        <span className="member-status-label">{status === 'idle' ? '閒置' : '離線'}</span>
                      )}
                      {isSyncing && <span className="presenting-badge">報告中</span>}
                    </button>
                  );
                })
              )}
            </div>
            {/* 設定這排頭像有哪些人、什麼順序（管理員可見）。固定在最右邊、不在捲動範圍內，永遠看得到 */}
            {canManageMembers && !membersLoading && (
              <button
                className="roster-manage-btn"
                onClick={() => setShowMemberVisibility(true)}
                title="管理簡報成員：設定這排頭像有哪些人、什麼順序"
              >
                ⚙️ 管理成員
              </button>
            )}
          </div>
          )}

          {/* 成員專案和 Tickets */}
          {selectedMember && (
            <div className="member-projects" ref={scrollContainerRef} onScroll={handleContentScroll}>
              {loading ? (
                <div className="loading-state">
                  <p>載入中...</p>
                </div>
              ) : (memberProjects.length === 0 && memberDeliverableItems.length === 0) ? (
                <div className="empty-state">
                  <h3>目前無負責的工作項目</h3>
                  <p>{selectedMemberData?.name || '此成員'} 目前沒有負責任何 Ticket 或工作項目</p>
                </div>
              ) : (
                <>
                  {memberProjects.length > 0 && (
                  <div className="projects-summary">
                    <h3>
                      {selectedMemberData?.name || '成員'} 的工作概況
                    </h3>
                    <div className="summary-stats">
                      <span className="stat-item">
                        <span className="stat-label">專案數量：</span>
                        <span className="stat-value">{memberProjects.length}</span>
                      </span>
                      <span className="stat-item">
                        <span className="stat-label">Ticket 總數：</span>
                        <span className="stat-value">
                          {totalTickets}（已完成 {completedTickets}）
                        </span>
                      </span>
                      <span className="stat-item">
                        <span className="stat-label">工作項目：</span>
                        <span className="stat-value">
                          {totalWorkItems}（已完成 {completedWorkItems}）
                        </span>
                      </span>
                    </div>
                  </div>
                  )}

                  {/* Project Tabs */}
                  <div className="project-tabs">
                    <button
                      className={`project-tab ${activeTab === 'all' ? 'active' : ''}`}
                      onClick={() => setActiveTab('all')}
                    >
                      全部
                      <span className="tab-badge">{totalTickets + totalWorkItems}</span>
                    </button>
                    {memberProjects.map(project => (
                      <button
                        key={project.id}
                        className={`project-tab ${activeTab === project.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(project.id)}
                        style={{ '--tab-color': project.color || '#667eea' }}
                      >
                        <span className="tab-icon">{project.icon || '📊'}</span>
                        <span className="tab-name">{project.name}</span>
                        <span className="tab-badge">{project.tickets.length + flattenWorkItems(project.workItems).length}</span>
                      </button>
                    ))}
                    {showDeliverables && memberDeliverableItems.length > 0 && (
                      <button
                        className={`project-tab deliverables-tab ${activeTab === 'deliverables' ? 'active' : ''}`}
                        onClick={() => setActiveTab('deliverables')}
                      >
                        交付項目
                        <span className="tab-badge">{memberDeliverableItems.length}</span>
                      </button>
                    )}
                  </div>

                  {/* 隱藏 Ticket 提示列 */}
                  {hiddenTicketIds.size > 0 && (
                    <div className="hidden-tickets-bar">
                      <div className="hidden-bar-summary" onClick={() => setShowHiddenList(prev => !prev)}>
                        <span className="hidden-count">已隱藏 {hiddenTicketIds.size} 個項目</span>
                        <span className="hidden-expand-icon">{showHiddenList ? '▲' : '▼'}</span>
                        <button
                          className="restore-all-btn"
                          onClick={(e) => { e.stopPropagation(); setHiddenTicketIds(new Set()); setShowHiddenList(false); }}
                        >
                          全部恢復
                        </button>
                      </div>
                      {showHiddenList && (
                        <div className="hidden-tickets-list">
                          {memberProjects.flatMap(p => [
                            ...p.tickets
                              .filter(t => hiddenTicketIds.has(t.id))
                              .map(t => ({ hideKey: t.id, no: `#${t.ticket_no}`, title: t.title, projectName: p.name, projectIcon: p.icon })),
                            ...flattenWorkItems(p.workItems)
                              .filter(w => hiddenTicketIds.has(`wi-${w.id}`))
                              .map(w => ({ hideKey: `wi-${w.id}`, no: w.work_item_no ? `工作項目 #${w.work_item_no}` : '子項', title: w.title, projectName: p.name, projectIcon: p.icon })),
                          ]).map(item => (
                            <div key={item.hideKey} className="hidden-ticket-item">
                              <span className="hidden-ticket-project">{item.projectIcon || '📊'} {item.projectName}</span>
                              <span className="hidden-ticket-title">{item.no} {item.title}</span>
                              <button
                                className="restore-btn"
                                onClick={() => toggleTicketHidden(item.hideKey)}
                              >
                                恢復
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {filteredProjects.length > 0 && (
                  <div className="projects-list">
                    {filteredProjects.map(project => {
                      const projectCompletedCount = project.tickets.filter(
                        t => t.status === 'completed' || t.status === 'closed'
                      ).length;
                      const projectWorkItems = flattenWorkItems(project.workItems);
                      const projectWiCompletedCount = projectWorkItems.filter(
                        w => w.status === 'completed' || w.status === 'closed'
                      ).length;

                      return (
                        <div key={project.id} className="project-section">
                          <div
                            className="project-header"
                            style={{ borderLeftColor: project.color }}
                            onClick={() => toggleProjectExpand(project.id)}
                          >
                            <div className="project-header-main">
                              <span className="expand-icon">
                                {expandedProjects.has(project.id) ? '▼' : '▶'}
                              </span>
                              <span className="project-icon">{project.icon || '📊'}</span>
                              {project.externalUrl ? (
                                <div className="project-name">
                                  <a
                                    href={project.externalUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="project-link"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {highlightText(project.name, highlightKeyword)}
                                  </a>
                                </div>
                              ) : (
                                <h4 className="project-name">
                                  {highlightText(project.name, highlightKeyword)}
                                </h4>
                              )}
                              <span className="task-count">
                                {project.tickets.length} 個 Ticket
                                {projectWorkItems.length > 0 && ` ＋ ${projectWorkItems.length} 個工作項目`}
                                （已完成 {projectCompletedCount + projectWiCompletedCount} 個）
                                {hoursSummary(projectWorkItems) && (
                                  <span className="task-count-hours">・{hoursSummary(projectWorkItems)}</span>
                                )}
                              </span>
                            </div>
                            {project.description && (
                              <div className="project-description">
                                {highlightText(project.description, highlightKeyword)}
                              </div>
                            )}
                          </div>

                          {expandedProjects.has(project.id) && (
                            <div className="tasks-list">
                              {project.tickets.map(ticket => {
                                const owner = resolveTicketOwner(ticket);
                                return (
                                <div
                                  key={ticket.id}
                                  className={`task-item ${expandedTickets.has(ticket.id) ? 'expanded' : ''}`}
                                >
                                  <div
                                    className="task-header"
                                    onClick={() => toggleTicketExpand(ticket.id)}
                                  >
                                    <span className="expand-icon">
                                      {ticket.description
                                        ? (expandedTickets.has(ticket.id) ? '▼' : '▶')
                                        : ''}
                                    </span>

                                    <div className="task-main">
                                      <div
                                        className="task-status-indicator"
                                        style={{ background: getStatusColor(ticket.status) }}
                                      />
                                      <div className="task-info">
                                        <h5 className="task-title">
                                          <span className="ticket-no-badge">#{ticket.ticket_no}</span>
                                          {highlightText(ticket.title, highlightKeyword)}
                                        </h5>
                                      </div>
                                    </div>

                                    <div className="task-meta">
                                      {owner && (
                                        <span className="task-owner" title={`Owner：${owner.name}`}>
                                          <Avatar
                                            avatarUrl={owner.avatarUrl}
                                            name={owner.name}
                                            size="xsmall"
                                          />
                                          <span className="task-owner-name">{owner.name}</span>
                                        </span>
                                      )}
                                      <span
                                        className="task-status"
                                        style={{
                                          borderColor: getStatusColor(ticket.status),
                                          color: getStatusColor(ticket.status),
                                        }}
                                      >
                                        {getStatusLabel(ticket.status)}
                                      </span>
                                      {ticket.start_date && ticket.end_date && (
                                        <span className="task-dates">
                                          {ticket.start_date} ~ {ticket.end_date}
                                        </span>
                                      )}
                                    </div>

                                    <button
                                      className="edit-task-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditTicket(ticket);
                                      }}
                                      title="編輯 Ticket"
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      className="hide-task-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleTicketHidden(ticket.id);
                                      }}
                                      title="從報告中隱藏此 Ticket"
                                    >
                                      隱藏
                                    </button>
                                  </div>

                                  {expandedTickets.has(ticket.id) && ticket.description && (
                                    <div className="task-details">
                                      <div className="task-description-full">
                                        <div className="description-label">Ticket 說明：</div>
                                        <div className="description-content">
                                          <MarkdownViewer content={ticket.description} />
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                );
                              })}
                              {/* work_item(工作項目)混排在同專案 ticket 之後;子項收在母項底下。
                                  群組用「分隔列」呈現而不是再嵌一層容器:日報是唸稿用的,需要的是同群組
                                  聚在一起 + 講得出群組名,不是可收合的樹。不加縮排,層級仍是三層,
                                  子項那層才不會被推到很深。Ticket 沒有群組概念,所以排在分隔列之前。*/}
                              {(() => {
                                const roots = project.workItems || [];
                                const groupKeys = [...new Set(roots.map(groupKeyOf))];
                                // 只有「全部未分組」時分隔列是純噪音 → 不渲染。
                                // 單一具名群組仍要顯示(「這些都屬於 規格 v1」是有用資訊)。
                                const showGroupHeaders = !(groupKeys.length <= 1 && groupKeys[0] === UNGROUPED_KEY);
                                const groupItems = new Map();
                                roots.forEach(w => {
                                  const k = groupKeyOf(w);
                                  if (!groupItems.has(k)) groupItems.set(k, []);
                                  groupItems.get(k).push(w, ...(w.children || []));
                                });
                                let lastKey = null;
                                return roots.map(wi => {
                                  const gKey = groupKeyOf(wi);
                                  const needHeader = showGroupHeaders && gKey !== lastKey;
                                  lastKey = gKey;
                                  const gItems = (groupItems.get(gKey) || []).filter(w => !w._context);
                                  const gHours = hoursSummary(gItems);
                                  return (
                                    <React.Fragment key={`g-${gKey}-${wi.id}`}>
                                      {needHeader && (
                                        <div className="wi-group-divider">
                                          <span className="wi-group-divider-name">{groupNameOf(wi)}</span>
                                          <span className="wi-group-divider-count">{gItems.length} 項</span>
                                          {gHours && <span className="wi-group-divider-hours">{gHours}</span>}
                                        </div>
                                      )}
                                      {renderWorkItemRow(wi)}
                                    </React.Fragment>
                                  );
                                });
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  )}
                </>
              )}
              {/* 交付項目區塊 */}
              {!loading && showDeliverables && filteredDeliverableItems.length > 0 && (
            <div className="deliverable-items-section">
              <h3 className="di-section-title">交付項目</h3>
              {(() => {
                // 按 Project > Phase > Deliverable 分群
                const grouped = {};
                filteredDeliverableItems.forEach((item) => {
                  const deliverable = item.deliverables || {};
                  const phase = deliverable.phases || {};
                  const project = phase.projects || {};
                  const key = `${phase.project_id}-${phase.id}-${deliverable.id}`;
                  if (!grouped[key]) {
                    grouped[key] = {
                      projectName: project.name || '未知專案',
                      projectColor: project.color || '#667eea',
                      phaseName: phase.name || '未知階段',
                      deliverableTitle: deliverable.title || '未知頁面',
                      items: [],
                    };
                  }
                  grouped[key].items.push(item);
                });

                return Object.entries(grouped).map(([key, group]) => (
                  <div key={key} className="di-group">
                    <div className="di-group-header">
                      <span className="di-dot" style={{ backgroundColor: group.projectColor }} />
                      <span className="di-project">{group.projectName}</span>
                      <span className="di-sep">&gt;</span>
                      <span className="di-phase">{group.phaseName}</span>
                      <span className="di-sep">&gt;</span>
                      <span className="di-deliverable">{group.deliverableTitle}</span>
                    </div>
                    {group.items.map((item) => (
                      <div key={item.id} className={`di-item ${item.is_completed ? 'completed' : ''}`}>
                        <span className={`di-check ${item.is_completed ? 'done' : ''}`}>
                          {item.is_completed ? '✓' : '○'}
                        </span>
                        <span className="di-role">{item.role_type}</span>
                        <span className="di-title">{item.title}</span>
                        {item.estimated_hours > 0 && (
                          <span className="di-hours">{item.estimated_hours}h</span>
                        )}
                        {item.actual_hours > 0 && (
                          <span className="di-hours di-actual-hours">實際 {item.actual_hours}h</span>
                        )}
                      </div>
                    ))}
                  </div>
                ));
              })()}
            </div>
          )}
            </div>
          )}

          {/* Project Selector Modal */}
          <BetaDailyReportProjectSelector
            isOpen={showProjectSelector}
            onClose={() => setShowProjectSelector(false)}
            onSuccess={handleRefresh}
          />

          {/* Member Visibility Modal */}
          <MemberVisibilityModal
            isOpen={showMemberVisibility}
            onClose={() => setShowMemberVisibility(false)}
            departmentId={effectiveDepartmentId}
            onSuccess={() => loadMembers(effectiveDepartmentId)}
          />
        </div>
      </div>
    </div>
  );
};

export default BetaDailyReportView;
